import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  notificationCellsApi,
  notificationEventsApi,
  notificationSettingsQueryKeys,
  type MessagingTemplate,
  type NotificationCell,
  type NotificationEventRow,
  type NotificationSettings,
} from "../../shared/api/notificationSettings";
import { apiError } from "../../shared/api/client";
import { TemplateSelect } from "./TemplateSelect";
import { StoppedTemplateWarning, TemplateTools, stoppedReason } from "./TemplateTools";
import { ParamTable, useTemplatePreview } from "./ParamTable";
import { PatientReceives } from "./PatientReceives";
import { ChannelConnectionLine, useMessagingChannels } from "./ChannelConnection";
import { RESULTS_LOOKUP_KEY } from "./OtpLookupNotice";
import {
  approvedTemplatesForChannel,
  lackingEssential,
  missingParamsForEvent,
  readinessProblem,
  requiredParamsForChannel,
} from "./NotificationsSettingsPage";

const CHANNEL_LABEL: Record<string, string> = {
  ZBS: "Zalo ZBS",
  SMS: "SMS",
  EMAIL: "Email",
};

/** Which global switch turns each channel on, so the row shows exactly the ticked ones. */
export const SWITCH: Record<string, keyof NotificationSettings> = {
  ZBS: "zaloEnabled",
  SMS: "smsEnabled",
  EMAIL: "emailEnabled",
};

/**
 * Why a template cannot carry the patient OTP, or null when it can (#19180): the code has to land
 * in a slot — canonical `otp.code`, or `otp`, the parameter name the approved Zalo OTP templates
 * carry and which Addy Message reports under its own name while unmapped.
 */
export function otpSlotProblem(t: MessagingTemplate, channel: string): string | null {
  const r = t.renditions.find((x) => x.channel === channel);
  const params = r?.canonicalParams ?? [];
  return params.includes("otp.code") || params.includes("otp") ? null : "thiếu ô mã OTP";
}

/** One entry of a template picker: the template, and why it cannot be chosen (null = it can). */
export interface PickerOption {
  template: MessagingTemplate;
  problem: string | null;
  /**
   * #19554 — parameters the clinic must type a fixed value for before this template can be saved.
   * Only Zalo parameter names nobody mapped (no dot); empty when the event fills everything.
   */
  needsFixed: string[];
}

/**
 * A parameter no system field can fill — a Zalo name nobody mapped, like `_TY_LE_GIAM_GIA_CT_KM_`
 * (#19554). The server keeps a fixed value only for these; a dotted name is a system field, and
 * typing one value for every message (one appointment time for every patient) is a wrong message.
 */
export function isFixedValueParam(param: string): boolean {
  return !param.includes(".");
}

/**
 * Whether a fixed value looks like per-visit data (#19565) — a date, a time, or Zalo's own example.
 * The same rule as the server's `looksDynamic`; the server refuses such a value unless confirmed.
 */
export function looksDynamic(value: string, sample?: string | null): boolean {
  const v = value.trim();
  if (/^\d{1,2}[/.-]\d{1,2}([/.-]\d{2,4})?$/.test(v)) return true;
  if (/^\d{1,2}\s*[:hH]\s*\d{2}$/.test(v)) return true;
  return !!sample && sample.trim() !== "" && sample.trim().toLowerCase() === v.toLowerCase();
}

/** Zalo's example value for a parameter, where the catalogue publishes one (#19565). */
export function sampleOfParam(t: MessagingTemplate | undefined, param: string): string | null {
  for (const r of t?.renditions ?? []) {
    for (const p of r.params) {
      if ((p.canonicalKey ?? p.key) === param && p.sampleValue) return p.sampleValue;
    }
  }
  return null;
}

/**
 * The name people read for a template (#19571): its provider name on this channel, else on any
 * channel; null when the platform publishes none — the caller then shows the code.
 */
export function templateName(t: MessagingTemplate | undefined, channel?: string): string | null {
  if (!t) return null;
  const on = channel ? t.renditions.find((r) => r.channel === channel)?.providerTemplateName : null;
  return on || t.renditions.find((r) => r.providerTemplateName)?.providerTemplateName || null;
}

/** Lower-case, without Vietnamese diacritics — "nhac lich" finds "Nhắc lịch". */
export function fold(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D")
    .toLowerCase();
}

/** "Tên khách hàng" for `patient.name`; the identifier itself when the catalogue has no label. */
export function labelOf(labels: Record<string, string> | undefined, key: string): string {
  return labels?.[key] ?? key;
}

/** The tightest `maxLength` any rendition declares for a parameter, for the input's limit. */
export function maxLengthOfParam(t: MessagingTemplate | undefined, param: string): number {
  let max = 250;
  for (const r of t?.renditions ?? []) {
    for (const p of r.params) {
      if ((p.canonicalKey ?? p.key) === param && p.maxLength != null) max = Math.min(max, p.maxLength);
    }
  }
  return max;
}

/**
 * What a picker offers for one event and channel (#19207).
 *
 * <p>Every approved, non-archived template on the channel is still listed — hiding the rest would
 * read as "the clinic has no templates" — but the ones the event cannot fill are disabled with the
 * reason, and the usable ones come first. Before this, 61 of the 64 templates on the demo clinic
 * were refused on save, with the error printed a screen below the row, so choosing one looked like
 * nothing happening at all.
 *
 * <p>`allowFixedValues` (#19554): a template whose only gaps are unmapped Zalo parameters becomes
 * choosable, with those parameters in `needsFixed` for the clinic to fill. Off by default — the
 * per-service matrix has no place to type them, so there it stays refused exactly as before.
 */
export function pickerOptions(
  templates: MessagingTemplate[],
  channel: string,
  event: NotificationEventRow,
  opts: { allowFixedValues?: boolean; labels?: Record<string, string> } = {},
): PickerOption[] {
  const label = (k: string) => labelOf(opts.labels, k);
  const out: PickerOption[] = approvedTemplatesForChannel(templates, channel)
    .filter((t) => !t.archived)
    .map((t) => {
      if (event.systemMessage)
        return { template: t, problem: otpSlotProblem(t, channel), needsFixed: [] };
      // #19564 — before the missing parameters: no fixed value repairs a template that is about
      // something else, so it is refused outright rather than offered "with values to type".
      const lacking = lackingEssential(t, event, channel);
      if (lacking.length > 0)
        return {
          template: t,
          problem:
            "không chứa " + lacking.map((g) => g.map(label).join(" + ")).join(" hoặc "),
          needsFixed: [],
        };
      const missing = missingParamsForEvent(t, event) ?? [];
      const fixable = opts.allowFixedValues ? missing.filter(isFixedValueParam) : [];
      const blocking = missing.filter((k) => !fixable.includes(k));
      return {
        template: t,
        problem:
          blocking.length > 0
            ? "thiếu " +
              blocking.slice(0, 3).map(label).join(", ") +
              (blocking.length > 3 ? ` +${blocking.length - 3}` : "")
            : null,
        needsFixed: fixable,
      };
    });
  // Stable: usable as-is first, then usable with fixed values, then the refused — each group
  // keeping the catalogue's order.
  return [
    ...out.filter((o) => o.problem === null && o.needsFixed.length === 0),
    ...out.filter((o) => o.problem === null && o.needsFixed.length > 0),
    ...out.filter((o) => o.problem !== null),
  ];
}

/**
 * Whether a clinic-wide cell only carries the event's old `HIS_*` placeholder (#19209) — the same
 * rule as `NotificationEventConfigService.chosenTemplate` on the server: a template stored on the
 * cell is always a choice; anything inherited is one only when it is not the placeholder.
 */
export function isInheritedPlaceholder(
  cell: NotificationCell | undefined,
  event: NotificationEventRow,
): boolean {
  if (!cell?.templateCode) return true;
  if (cell.source === "SERVICE" || cell.source === "CHANNEL_DEFAULT") return false;
  return cell.templateCode === event.builtInTemplateCode;
}

/**
 * One template picker per enabled channel, for one event (#18933 §2).
 *
 * <p>Replaces the single dropdown that used to sit here. That one bound ONE template code to the
 * event whatever channel the message went out on — but a template at the provider is a set of
 * per-channel renditions, so a code chosen for its ZBS rendition was also being used for SMS and
 * e-mail, where that rendition does not exist. One picker per channel is not a nicety; it is the
 * only shape that can express what the system actually sends.
 *
 * <p>Each picker offers only templates with an approved rendition ON THAT CHANNEL, which is what
 * makes it impossible to pick an SMS template for the Zalo slot.
 */
export function EventChannelTemplates({
  event,
  templates,
  settings,
  onSaved,
  onError,
  templatesReady = true,
  labels,
  examples,
  onDirtyChange,
  serviceId = null,
}: {
  /**
   * #19585 — the scope being edited: null for the clinic default, or one service's own row. A
   * service cell with no template of its own follows the default (#19581), and says so.
   */
  serviceId?: string | null;
  /**
   * #19579 — told whenever an unsaved fixed-value draft appears or goes, so the page can ask
   * before closing the row that holds it.
   */
  onDirtyChange?: (dirty: boolean) => void;
  /** #19565 — field → Vietnamese label, from the variable catalogue. */
  labels?: Record<string, string>;
  /** #19567 — field → example value, for the preview. */
  examples?: Record<string, string>;
  event: NotificationEventRow;
  templates: MessagingTemplate[];
  settings: NotificationSettings | null;
  onSaved: (m: string) => void;
  onError: (m: string | null) => void;
  /** False while the catalogue is still loading — "no template" is only true once it has. */
  templatesReady?: boolean;
}) {
  const qc = useQueryClient();
  // Under the picker it belongs to (#19207): the page-level line sits at the bottom of a long
  // card, a screen away from the row, so a refused save looked like nothing had happened.
  const [inlineError, setInlineError] = useState<Record<string, string>>({});
  // #19554 — a template that needs fixed values is not saved on pick: the clinic types them
  // first. One draft per channel; absent = nothing being edited there.
  const [drafts, setDrafts] = useState<Record<string, FixedDraft | undefined>>({});
  const dirty = Object.values(drafts).some(Boolean);
  useEffect(() => onDirtyChange?.(dirty), [dirty, onDirtyChange]);
  // Unmounted with a draft (row closed, tab switched…): the draft is gone, so is the flag.
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  // #19613 — a channel Addy Message cannot send on offers no template picker, for every event.
  const connection = useMessagingChannels();
  const cellsQ = useQuery({
    queryKey: notificationSettingsQueryKeys.cells(event.eventKey),
    queryFn: () => notificationCellsApi.list(event.eventKey),
  });

  const save = useMutation({
    mutationFn: async (v: {
      channel: string;
      templateCode: string | null;
      staticParams?: Record<string, string>;
      acknowledged?: string[];
      /** A service may switch a channel off for itself; the clinic default is always on. */
      enabled?: boolean;
    }) => {
      await notificationCellsApi.save(event.eventKey, {
        serviceId,
        channel: v.channel,
        enabled: v.enabled ?? true,
        templateCode: v.templateCode,
        boundParams: requiredParamsForChannel(
          templates.find((t) => t.code === v.templateCode),
          v.channel,
        ),
        staticParams: v.staticParams ?? null,
        acknowledgedStaticParams: v.acknowledged ?? null,
      });
      // Choosing a template for a channel must also put that channel in the event's priority
      // order, or the screen would promise a send the dispatcher never makes: channelOrder is
      // still the list it walks, and a clinic that only ever had e-mail on has an order of
      // ["EMAIL"] inherited from the old boolean settings.
      if (!event.channelOrder.includes(v.channel)) {
        await notificationEventsApi.update(event.eventKey, {
          enabled: event.enabled,
          channelOrder: [...event.channelOrder, v.channel],
          templateCode: event.templateCode,
          boundParams: null,
        });
      }
    },
    onSuccess: (_data, v) => {
      qc.invalidateQueries({ queryKey: notificationSettingsQueryKeys.cells(event.eventKey) });
      qc.invalidateQueries({ queryKey: notificationSettingsQueryKeys.events() });
      if (event.eventKey === "PATIENT_OTP") qc.invalidateQueries({ queryKey: RESULTS_LOOKUP_KEY });
      setInlineError((m) => ({ ...m, [v.channel]: "" }));
      setDrafts((d) => ({ ...d, [v.channel]: undefined }));
      onSaved("Đã lưu mẫu tin cho kênh.");
      onError(null);
    },
    onError: (e: unknown, v) => {
      setInlineError((m) => ({ ...m, [v.channel]: apiError(e).detail }));
      onError(null);
    },
  });

  // A system message (the patient OTP, #19180) is not a patient-care message the global switches
  // above govern: it always offers its own channels, and nothing else.
  const enabledChannels = event.systemMessage
    ? event.allowedChannels
    : ["ZBS", "SMS", "EMAIL"].filter((c) => settings && settings[SWITCH[c]]);

  if (enabledChannels.length === 0) {
    return (
      <small className="text-xs text-muted-foreground">
        Chưa bật kênh nào ở phần “Kênh gửi” bên trên.
      </small>
    );
  }

  const cells = cellsQ.data ?? [];
  // `== null`, not `=== null`: the API leaves a null serviceId out of the JSON, so the clinic-wide
  // row arrives with it undefined — a strict check never matched and the picker never showed what
  // the clinic had saved (found while testing #19180).
  const defaultCell = (channel: string) =>
    cells.find((c) => c.serviceId == null && c.channel === channel);
  // The cell of the scope being edited: the clinic-wide one, or the service's own (none = it has
  // not written this channel and follows the default).
  const cellFor = (channel: string) =>
    serviceId
      ? cells.find((c) => c.serviceId === serviceId && c.channel === channel)
      : defaultCell(channel);
  const templateFor = (channel: string) => cellFor(channel)?.templateCode ?? "";

  return (
    <div className="grid min-w-0 gap-2">
      {enabledChannels.map((channel) => {
        // Archived templates are refused at send time whatever their approval says (Addy
        // Message D-130), so they are not offered at all.
        const picks = pickerOptions(templates, channel, event, { allowFixedValues: true, labels });
        const options = picks.map((p) => p.template);
        const usable = picks.filter((p) => p.problem === null).length;
        // For the OTP, the inherited built-in default is shown when it is a real, usable template
        // on this channel — it is what the code is sent under, and the Website screen says so.
        // A default that is not among the options reads as "— Chưa chọn —", matching the
        // readiness check's "chưa chọn mẫu".
        const stored = templateFor(channel);
        // #19209: the inherited `HIS_*` placeholder is not a choice on any channel — Addy Message
        // holds no template by that code, and the dispatcher sends nothing under it. Showing it, or
        // calling it "mẫu mặc định", would promise a message that never goes out.
        // A service's own row has no placeholder to inherit: its template is a choice or nothing.
        const unchosen =
          !serviceId && !event.systemMessage && isInheritedPlaceholder(cellFor(channel), event);
        const chosen = unchosen || !options.some((t) => t.code === stored) ? "" : stored;
        // #19585 — on/off is the scope's own: a service may switch a channel off for itself.
        const own = cellFor(channel);
        const enabledHere = serviceId
          ? (own?.enabled ?? defaultCell(channel)?.enabled ?? true)
          : true;
        // What a service with no template of its own actually sends (#19581): the default's.
        const defaultCode = defaultCell(channel)?.templateCode ?? "";
        const inheritedName =
          serviceId && !chosen && !isInheritedPlaceholder(defaultCell(channel), event)
            ? (templateName(templates.find((t) => t.code === defaultCode), channel) ?? defaultCode)
            : null;
        const draft = drafts[channel];
        const shown = draft?.templateCode ?? chosen;
        const shownPick = picks.find((p) => p.template.code === shown);
        const storedValues = cellFor(channel)?.staticParams ?? {};
        // #19576 — a real choice that can no longer go out. Only once the catalogue has loaded
        // ("không còn tồn tại" is otherwise just "not fetched yet"), and never for the OTP's
        // inherited built-in code, which the server treats as "no template" when it is absent.
        const builtIn = cellFor(channel)?.source === "BUILT_IN";
        const stopped =
          templatesReady && stored && !unchosen && !(event.systemMessage && builtIn)
            ? stoppedReason(stored, templates, channel)
            : null;

        const pick = (code: string) => {
          const p = picks.find((x) => x.template.code === code);
          if (!code || !p || p.needsFixed.length === 0) {
            setDrafts((d) => ({ ...d, [channel]: undefined }));
            save.mutate({ channel, templateCode: code || null, enabled: enabledHere });
            return;
          }
          // Re-choosing the saved template starts from its saved values.
          const start: Record<string, string> = code === chosen ? storedValues : {};
          setDrafts((d) => ({
            ...d,
            [channel]: {
              templateCode: code,
              values: Object.fromEntries(p.needsFixed.map((k) => [k, start[k] ?? ""])),
              // Never pre-confirmed, not even a saved value: one stored before #19565 was never
              // confirmed at all, and re-opening it is the moment to look at it again.
              acknowledged: [],
            },
          }));
          setInlineError((m) => ({ ...m, [channel]: "" }));
        };

        // #19585 — a service may switch one channel off for itself. Only the stored template of
        // THIS row is sent back: re-sending the inherited one would make it the service's own
        // (review of #19566).
        const toggle = serviceId ? (
          <label className="flex items-center gap-1.5 text-xs">
            <input
              type="checkbox"
              checked={enabledHere}
              disabled={save.isPending || cellsQ.isLoading}
              onChange={(e) =>
                save.mutate({
                  channel,
                  templateCode: own?.templateCode ?? null,
                  staticParams: own?.staticParams,
                  enabled: e.target.checked,
                })
              }
            />
            Gửi {CHANNEL_LABEL[channel]} cho dịch vụ này
          </label>
        ) : null;
        const conn = connection.data?.channels.find((l) => l.channel === channel);
        if (conn?.state === "OFF") {
          return (
            <div
              key={channel}
              className="grid min-w-0 gap-0.5 rounded-md border border-dashed p-3"
              data-testid={`channel-${channel}`}
            >
              <span className="text-xs font-medium uppercase tracking-wide">
                {CHANNEL_LABEL[channel]}
              </span>
              <ChannelConnectionLine line={conn} />
              <small className="text-xs text-muted-foreground" data-testid={`channel-blocked-${channel}`}>
                Chưa chọn được mẫu cho kênh này cho tới khi kênh được kết nối.
              </small>
            </div>
          );
        }
        if (!enabledHere) {
          return (
            <div key={channel} className="grid min-w-0 gap-0.5" data-testid={`channel-${channel}`}>
              <span className="text-xs text-muted-foreground">{CHANNEL_LABEL[channel]}</span>
              {toggle}
              <small className="text-xs text-muted-foreground">
                Không gửi {CHANNEL_LABEL[channel]} cho dịch vụ này.
              </small>
              {inlineError[channel] && (
                <small className="text-xs text-destructive" role="alert">
                  {inlineError[channel]}
                </small>
              )}
            </div>
          );
        }

        // #19586 — the parameter table is the platform's own preview, Zalo only (#19584). A service
        // following the default previews the default's template, with the service's scope.
        const previewCode = shown || (inheritedName && !draft ? defaultCode : "");
        const paramTable =
          channel === "ZBS" && previewCode ? (
            <ZbsParams
              event={event}
              serviceId={serviceId}
              templateCode={previewCode}
              template={templates.find((t) => t.code === previewCode)}
              draft={draft}
              labels={labels ?? {}}
              canEditFixed={!draft && !save.isPending && previewCode === shown}
              onEditFixed={() => pick(shown)}
            />
          ) : null;

        return (
          <div
            key={channel}
            className="grid min-w-0 gap-1.5 rounded-md border p-3"
            data-testid={`channel-${channel}`}
          >
            <div className="grid min-w-0 gap-1">
              <span className="flex flex-wrap items-center gap-2 text-xs">
                <span className="font-medium uppercase tracking-wide">{CHANNEL_LABEL[channel]}</span>
                {shownPick && !shownPick.problem && (
                  <span className="text-emerald-700 dark:text-emerald-400">● Đã duyệt</span>
                )}
              </span>
              {toggle}
              <TemplateSelect
                picks={picks}
                value={shown}
                disabled={save.isPending || cellsQ.isLoading}
                onChange={pick}
                emptyLabel={serviceId ? "(theo mặc định phòng khám)" : "— Chưa chọn —"}
                channel={channel}
                invalid={!!shownPick?.problem}
                // #19587 — the Zalo card shows the preview itself, with its own link.
                showPreviewLink={!(channel === "ZBS" && previewCode)}
              />
              {inheritedName && !draft && (
                <small className="text-xs text-muted-foreground" data-testid="inherited-template">
                  Đang dùng mẫu mặc định: {inheritedName}
                </small>
              )}
              {serviceId && !draft && readinessProblem(own?.readiness) && (
                <small className="text-xs text-amber-700" data-testid="cell-readiness">
                  ⚠️ {readinessProblem(own?.readiness)}
                </small>
              )}
            </div>
            {paramTable}
            {draft ? (
              <FixedValuesEditor
                template={templates.find((t) => t.code === draft.templateCode)}
                draft={draft}
                busy={save.isPending}
                onChange={(next) => setDrafts((d) => ({ ...d, [channel]: next }))}
                onCancel={() => {
                  setDrafts((d) => ({ ...d, [channel]: undefined }));
                  setInlineError((m) => ({ ...m, [channel]: "" }));
                }}
                onSave={() =>
                  save.mutate({
                    channel,
                    templateCode: draft.templateCode,
                    staticParams: draft.values,
                    acknowledged: draft.acknowledged,
                    enabled: enabledHere,
                  })
                }
              />
            ) : (
              // Zalo shows its fixed values in the parameter table; the other channels have no
              // platform preview yet and keep the line.
              !paramTable &&
              shownPick &&
              shownPick.needsFixed.length > 0 && (
                <small className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
                  <span>
                    Giá trị cố định:{" "}
                    {shownPick.needsFixed
                      .map((k) => `${k} = ${storedValues[k] ?? "(trống)"}`)
                      .join(" · ")}
                  </span>
                  <button
                    type="button"
                    className="underline"
                    disabled={save.isPending}
                    onClick={() => pick(shown)}
                  >
                    Sửa
                  </button>
                </small>
              )
            )}
            {/* Zalo's test send lives in "Bệnh nhân nhận" (#19587); the other channels keep theirs. */}
            {!draft && chosen && !paramTable && (
              <TemplateTools
                event={event}
                channel={channel}
                serviceId={serviceId}
                template={templates.find((t) => t.code === chosen)}
                staticParams={storedValues}
                labels={labels ?? {}}
                examples={examples ?? {}}
                showPreview={!paramTable}
              />
            )}
            {!draft && stopped && (
              <StoppedTemplateWarning
                code={stored}
                reason={stopped}
                channel={channel}
                templates={templates}
              />
            )}
            {!draft && shownPick?.problem && (
              <small className="text-xs text-destructive" data-testid="stored-template-problem">
                Mẫu đang dùng {shownPick.problem} — tin gửi đi sẽ sai nội dung. Hãy chọn mẫu khác.
              </small>
            )}
            {inlineError[channel] && (
              <small className="text-xs text-destructive" role="alert">
                {inlineError[channel]}
              </small>
            )}
            {templatesReady && options.length > 0 && usable === 0 && (
              <small className="text-xs text-amber-700">
                Chưa có mẫu {CHANNEL_LABEL[channel]} nào phù hợp với sự kiện này — cần đăng ký mẫu
                mới có đủ thông tin sự kiện cung cấp.
              </small>
            )}
            {/* Silence here would read as "no templates exist"; the real reason is almost always
                that none has an approved rendition on this particular channel. */}
            {templatesReady && options.length === 0 && (
              <small className="text-xs text-amber-700">
                Chưa có mẫu nào được duyệt cho {CHANNEL_LABEL[channel]}.
              </small>
            )}
            {event.systemMessage && channel === "SMS" && !chosen && (
              <small className="text-xs text-amber-700">
                ⚠️ Bệnh nhân không dùng Zalo sẽ không nhận được mã.
              </small>
            )}
          </div>
        );
      })}
      {/* #19586 — a channel switched off for the whole clinic is one line, with no picker and no
          box to tick: nothing here can send on it until "Kênh gửi" turns it on. */}
      {!event.systemMessage &&
        ["ZBS", "SMS", "EMAIL"]
          .filter((c) => !enabledChannels.includes(c))
          .map((c) => (
            <small key={c} className="text-xs text-muted-foreground" data-testid={`channel-off-${c}`}>
              {CHANNEL_LABEL[c]} · đang tắt toàn phòng khám
            </small>
          ))}
      {event.systemMessage && !event.allowedChannels.includes("EMAIL") && (
        <small className="text-xs text-muted-foreground">Email: không dùng cho OTP.</small>
      )}
    </div>
  );
}

/** A template chosen but not yet saved, with the fixed values being typed (#19554/#19565). */
export interface FixedDraft {
  templateCode: string;
  values: Record<string, string>;
  /** Values the clinic confirmed are meant to be identical in every message (#19565). */
  acknowledged: string[];
}

/**
 * The inputs for a template's fixed values (#19554) — one per parameter no event fills. Saving is
 * held back until every one has a value, and (#19565) until every value that looks like per-visit
 * data — a date, a time, Zalo's own example — has been explicitly confirmed. The server applies
 * both rules too.
 */
export function FixedValuesEditor({
  template,
  draft,
  busy,
  onChange,
  onCancel,
  onSave,
}: {
  template: MessagingTemplate | undefined;
  draft: FixedDraft;
  busy: boolean;
  onChange: (d: FixedDraft) => void;
  onCancel: () => void;
  onSave: () => void;
}) {
  const keys = Object.keys(draft.values);
  const complete = keys.every((k) => draft.values[k].trim() !== "");
  const suspicious = keys.filter((k) => looksDynamic(draft.values[k], sampleOfParam(template, k)));
  const confirmed = suspicious.every((k) => draft.acknowledged.includes(k));
  const setValue = (k: string, v: string) =>
    onChange({
      ...draft,
      values: { ...draft.values, [k]: v },
      // Editing a value withdraws its confirmation: it was given for the old text.
      acknowledged: draft.acknowledged.filter((x) => x !== k),
    });
  const setAck = (k: string, on: boolean) =>
    onChange({
      ...draft,
      acknowledged: on
        ? [...draft.acknowledged.filter((x) => x !== k), k]
        : draft.acknowledged.filter((x) => x !== k),
    });
  return (
    <div className="grid gap-2 rounded-md border border-dashed p-2" data-testid="fixed-values-editor">
      <small className="text-xs text-amber-800">
        ⚠️ Giá trị bạn nhập sẽ <b>GIỐNG NHAU trong MỌI tin</b> gửi cho mọi bệnh nhân (phù hợp cho
        mức ưu đãi, lời nhắn chung…). Nếu tham số là thông tin đã có trong hệ thống — địa chỉ / số
        điện thoại phòng khám, ngày giờ khám — đừng nhập tay: hãy liên hệ Addy để ánh xạ.
      </small>
      {keys.map((k) => {
        const sample = sampleOfParam(template, k);
        const flagged = suspicious.includes(k);
        return (
          <div key={k} className="grid gap-0.5">
            <label className="grid gap-0.5">
              <span className="text-xs">
                Tham số Zalo <span className="font-mono">{k}</span>
                <span className="text-muted-foreground">
                  {" "}
                  · tối đa {maxLengthOfParam(template, k)} ký tự
                </span>
              </span>
              {sample && (
                <span className="text-xs text-muted-foreground">Ví dụ của Zalo: {sample}</span>
              )}
              <input
                className={
                  "h-8 w-full min-w-0 rounded-md border bg-background px-2 text-sm" +
                  (flagged ? " border-amber-500" : "")
                }
                value={draft.values[k]}
                maxLength={maxLengthOfParam(template, k)}
                onChange={(e) => setValue(k, e.target.value)}
              />
            </label>
            {flagged && (
              <label className="flex items-start gap-1.5 text-xs text-amber-800">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={draft.acknowledged.includes(k)}
                  onChange={(e) => setAck(k, e.target.checked)}
                />
                <span>
                  “{draft.values[k]}” trông như ngày/giờ hoặc giá trị ví dụ. Tôi xác nhận giá trị
                  này giống nhau cho mọi tin.
                </span>
              </label>
            )}
          </div>
        );
      })}
      <div className="flex gap-2">
        <Button type="button" size="sm" disabled={busy || !complete || !confirmed} onClick={onSave}>
          Lưu
        </Button>
        <Button type="button" size="sm" variant="outline" disabled={busy} onClick={onCancel}>
          Huỷ
        </Button>
      </div>
    </div>
  );
}

/**
 * One Zalo card's "Tham số của mẫu" (#19586), from the platform preview (#19584). Its own component
 * because the preview is a query, and a hook cannot live inside the channel loop.
 */
function ZbsParams({
  event,
  serviceId,
  templateCode,
  template,
  draft,
  labels,
  canEditFixed,
  onEditFixed,
}: {
  event: NotificationEventRow;
  serviceId: string | null;
  templateCode: string;
  template: MessagingTemplate | undefined;
  draft: FixedDraft | undefined;
  labels: Record<string, string>;
  canEditFixed: boolean;
  onEditFixed: () => void;
}) {
  const query = useTemplatePreview(
    event,
    {
      channel: "ZBS",
      serviceId,
      templateCode,
      // A draft previews what is being typed; otherwise the server uses what is stored.
      staticParams: draft ? draft.values : undefined,
    },
    true,
  );
  // #19587 — configuration on the left, what the patient gets on the right; stacked when narrow.
  return (
    <div className="grid min-w-0 items-start gap-3 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
      <ParamTable
        channel="ZBS"
        template={template}
        query={query}
        labels={labels}
        draft={!!draft}
        canEditFixed={canEditFixed}
        onEditFixed={onEditFixed}
      />
      <PatientReceives
        event={event}
        serviceId={serviceId}
        template={template}
        query={query}
        draft={!!draft}
      />
    </div>
  );
}
